<template>
  <paper-modal-container :is-visible="isVisible" :title="editMode ? '작품 등록 수정' : '작품 등록 신청'" :hide-close-button="false"
    @close="cancelPopup">
    <br />
    <!-- 유튜브 링크 입력 -->
    <div :class="$style.input">
      <h4 :class="$style.required">유튜브 영상 링크</h4>
      <p :class="$style.infoText">{{ editMode ? '수정 모드에서는 링크를 변경할 수 없습니다' : '유튜브 영상 URL을 입력하면 정보를 가져올 수 있습니다' }}</p>
      <input :class="[$style.textInput, { [$style.disabledInput]: editMode }]" v-model="link"
        placeholder="https://www.youtube.com/watch?v=..." :disabled="uploading || editMode" @blur="onLinkBlur" />
    </div>

    <!-- 영상 정보 미리보기 -->
    <div v-if="videoPreview" :class="$style.preview">
      <div :class="$style.previewContent">
        <div :class="$style.videoThumbWrap">
          <img v-if="videoPreview.videoThumbnail" :src="videoPreview.videoThumbnail" :class="$style.videoThumb"
            alt="영상 썸네일" />
          <span :class="$style.previewBadge">YouTube</span>
        </div>
        <div :class="$style.previewInfo">
          <h4 :class="$style.previewTitle">{{ videoPreview.title }}</h4>
          <div :class="$style.previewMeta">
            <div :class="$style.channelPreview">
              <img v-if="videoPreview.channelThumbnail" :src="videoPreview.channelThumbnail"
                :class="$style.channelThumb" alt="채널 이미지" @error="handleChannelImageError" />
              <div v-else :class="$style.channelThumbFallback">
                {{ videoPreview.channel ? videoPreview.channel.charAt(0).toUpperCase() : '?' }}
              </div>
              <span :class="$style.channelName">{{ videoPreview.channel }}</span>
            </div>
            <span v-if="videoPreview.publishedAt" :class="$style.previewDate">{{
              formatPublishedAt(videoPreview.publishedAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 타입 선택 -->
    <div :class="$style.input">
      <h4>타입 선택 <span style="font-size: 12px"></span></h4>
      <div :class="$style.typeSelector">
        <button v-for="type in typeOptions" :key="type.value"
          :class="[$style.typeButton, { [$style.typeButtonActive]: selectedType === type.value }]"
          @click="selectedType = selectedType === type.value ? '' : type.value" :disabled="uploading" type="button">
          <span :class="$style.typeLabel">{{ type.label }}</span>
        </button>
      </div>
    </div>

    <!-- 오리지널/커버 선택 -->
    <div :class="$style.input">
      <h4>분류 <span style="font-size: 12px"></span></h4>
      <div :class="$style.typeSelector">
        <button type="button" :class="[$style.typeButton, { [$style.typeButtonActive]: isCover === false }]"
          @click="isCover = false" :disabled="uploading">
          <span :class="$style.typeLabel">오리지널</span>
        </button>
        <button type="button" :class="[$style.typeButton, { [$style.typeButtonActive]: isCover === true }]"
          @click="isCover = true" :disabled="uploading">
          <span :class="$style.typeLabel">커버</span>
        </button>
      </div>
    </div>

    <!-- 원곡 입력 (커버인 경우만) -->
    <div v-if="isCover" :class="$style.input">
      <h4>원곡 유튜브 링크 <span style="font-size: 12px">(선택)</span></h4>
      <p :class="$style.infoText">원곡의 유튜브 영상 URL을 입력하면 정보를 가져올 수 있습니다</p>
      <input :class="$style.textInput" v-model="originalSong" placeholder="https://www.youtube.com/watch?v=..."
        :disabled="uploading" @blur="onOriginalSongBlur" />
      <!-- 원곡 정보 미리보기 -->
      <div v-if="originalSongPreview" :class="$style.preview">
        <div :class="$style.previewContent">
          <div :class="$style.videoThumbWrap">
            <img v-if="originalSongPreview.videoThumbnail" :src="originalSongPreview.videoThumbnail"
              :class="$style.videoThumb" alt="원곡 썸네일" />
            <span :class="$style.previewBadge">원곡</span>
          </div>
          <div :class="$style.previewInfo">
            <h4 :class="$style.previewTitle">{{ originalSongPreview.title }}</h4>
            <div :class="$style.previewMeta">
              <div :class="$style.channelPreview">
                <img v-if="originalSongPreview.channelThumbnail" :src="originalSongPreview.channelThumbnail"
                  :class="$style.channelThumb" alt="채널 이미지" @error="handleOriginalSongChannelImageError" />
                <div v-else :class="$style.channelThumbFallback">
                  {{ originalSongPreview.channel ? originalSongPreview.channel.charAt(0).toUpperCase() : '?' }}
                </div>
                <span :class="$style.channelName">{{ originalSongPreview.channel }}</span>
              </div>
              <span v-if="originalSongPreview.publishedAt" :class="$style.previewDate">{{
                formatPublishedAt(originalSongPreview.publishedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 라인업 입력 (LiveForm과 동일: 프로필 이미지 + 타입 + 드롭다운 옵션) -->
    <div :class="$style.input">
      <h4>라인업 <span style="font-size: 12px">(검색 또는 직접 입력)</span></h4>
      <div v-if="selectedArtists.length > 0" :class="$style.artistList">
        <div v-for="(artist, index) in selectedArtists" :key="`artist-${index}-${getArtistName(artist)}`"
          :class="$style.artistItem">
          <div :class="$style.artistInfo">
            <img v-if="artist.profileImage" :src="artist.profileImage" :alt="getArtistName(artist)"
              :class="$style.profileImage" />
            <img v-else :src="getArtistDefaultImage(artist.type)" :alt="`${getArtistName(artist)} 기본 프로필`"
              :class="$style.profileImage" />
            <div :class="$style.artistDetails">
              <div :class="$style.artistNameRow">
                <span :class="$style.artistName">{{ getArtistName(artist) }}</span>
                <span v-if="artist.isNew" :class="$style.newBadge">신규</span>
              </div>
              <span :class="$style.artistType">{{ formatArtistType(artist.type) }}</span>
            </div>
          </div>
          <button v-if="!isDefaultArtist(artist)" type="button" @click="removeArtist(index)"
            :class="$style.removeButton" :disabled="uploading">
            삭제
          </button>
        </div>
      </div>
      <div :class="$style.addArtistForm">
        <AutoComplete ref="artistAutocompleteRef" v-model="currentArtistInput" :suggestions="filteredArtists"
          @complete="searchArtists" @focus="onArtistFocus" @item-select="addArtistFromSuggestion"
          @keyup.enter.prevent="addArtistManually" placeholder="아티스트명 검색 또는 신규 입력" :disabled="uploading"
          :class="[$style.autocomplete, $style.artistSearchAutocomplete]" dropdown :forceSelection="false"
          optionLabel="label" panelClass="video-artist-autocomplete-panel" appendTo="self">
          <template #option="{ option }">
            <div class="artist-option">
              <img v-if="option.profileImage" :src="option.profileImage" :alt="option.name"
                class="option-profile-image" />
              <img v-else :src="getArtistDefaultImage(option.type)" :alt="`${option.name} 기본 프로필`"
                class="option-profile-image" />
              <div class="option-details">
                <span class="option-name">{{ option.name }}</span>
                <span class="option-type">{{ formatArtistType(option.type) }}</span>
              </div>
            </div>
          </template>
        </AutoComplete>
        <button type="button" :class="$style.addButton" @click="addArtistManually"
          :disabled="uploading || !(selectedArtistData || (currentArtistInput && currentArtistInput.trim()))">
          추가
        </button>
      </div>
    </div>

    <!-- 함께한 사람들 입력 (역할 섹션 먼저 추가 → 각 섹션에 사람 추가, LiveForm과 동일) -->
    <div :class="$style.input">
      <h4>함께한 사람들 <span style="font-size: 12px">(예: 믹싱, 에디팅, 일러스트)</span></h4>
      <div v-if="crewSections.length > 0" :class="$style.crewDepthBlock">
        <div :class="$style.crewSectionList">
          <div v-for="section in crewSections" :key="section.role" :class="$style.crewSection">
            <div :class="$style.crewSectionHeader">
              <h4 :class="$style.crewSectionTitle" style="margin-bottom: 0;">{{ section.role }}</h4>
              <button type="button" :class="$style.removeRoleButton" @click="removeCrewRoleSection(section.role)"
                :disabled="uploading">
                역할 삭제
              </button>
            </div>
            <div v-if="section.members.length > 0" :class="$style.artistList">
              <div v-for="item in section.members" :key="`${section.role}-${item.index}`" :class="$style.artistItem">
                <div :class="$style.artistInfo">
                  <img v-if="item.person.profileImage" :src="item.person.profileImage" :alt="getCrewName(item.person)"
                    :class="$style.profileImage" />
                  <img v-else :src="getArtistDefaultImage(item.person.type)" :alt="`${getCrewName(item.person)} 기본 프로필`"
                    :class="$style.profileImage" />
                  <div :class="$style.artistDetails">
                    <div :class="$style.artistNameRow">
                      <span :class="$style.artistName">{{ getCrewName(item.person) }}</span>
                      <span v-if="item.person.isNew" :class="$style.newBadge">신규</span>
                    </div>
                    <span :class="$style.artistType">{{ formatArtistType(item.person.type) }}</span>
                  </div>
                </div>
                <button type="button" @click="removeCollaborator(item.index)" :class="$style.removeButton"
                  :disabled="uploading">
                  삭제
                </button>
              </div>
            </div>
            <div :class="[$style.addArtistForm, $style.crewAddForm]">
              <AutoComplete v-model="crewInputByRole[section.role]"
                :suggestions="filteredCollaboratorsByRole[section.role] || []"
                @item-select="addCollaboratorFromSuggestionByRole($event, section.role)"
                @complete="searchCollaboratorsByRole($event, section.role)" placeholder="아티스트명 검색 또는 신규 입력"
                :disabled="uploading" :class="[$style.autocomplete, $style.artistSearchAutocomplete]" dropdown
                :forceSelection="false" optionLabel="label" panelClass="video-collaborator-autocomplete-panel"
                appendTo="self">
                <template #option="{ option }">
                  <div class="artist-option">
                    <img v-if="option.profileImage" :src="option.profileImage" :alt="option.name"
                      class="option-profile-image" />
                    <img v-else :src="getArtistDefaultImage(option.type)" :alt="`${option.name} 기본 프로필`"
                      class="option-profile-image" />
                    <div class="option-details">
                      <span class="option-name">{{ option.name }}</span>
                      <span class="option-type">{{ formatArtistType(option.type) }}</span>
                    </div>
                  </div>
                </template>
              </AutoComplete>
              <button type="button" :class="$style.addButton" @click="addCollaboratorManuallyByRole(section.role)"
                :disabled="uploading || !canAddCollaboratorForRole(section.role)">
                추가
              </button>
            </div>
          </div>
        </div>
      </div>
      <p v-else :class="$style.infoText">먼저 역할 섹션을 추가한 뒤 아티스트를 등록할 수 있습니다.</p>

      <div :class="$style.crewRoleSelector">
        <AutoComplete ref="crewRoleAutocompleteRef" v-model="currentCrewRoleInput" :suggestions="filteredCrewRoles"
          @complete="searchCrewRoles" @focus="onCrewRoleFocus" @item-select="addCrewRoleFromSuggestion"
          placeholder="역할 검색 또는 신규 입력" :disabled="uploading" :class="$style.crewRoleAutocomplete" dropdown
          :forceSelection="false" panelClass="video-crew-role-panel" appendTo="self" />
        <button type="button" :class="$style.addRoleButton" @click="addCrewRoleSection"
          :disabled="uploading || !pendingCrewRole">
          역할 섹션 추가
        </button>
      </div>
    </div>

    <!-- 설명 입력 -->
    <div :class="$style.input">
      <h4>설명 <span style="font-size: 12px">(선택)</span></h4>
      <textarea :class="$style.textarea" v-model="description" placeholder="영상에 대한 설명을 입력해주세요..." :disabled="uploading"
        rows="6"></textarea>
    </div>

    <div :class="$style.buttons">
      <button :class="$style.cancel" @click="cancelPopup" :disabled="uploading">
        취소
      </button>
      <button :class="$style.register" @click="editMode ? updateVideo() : upload()" :disabled="uploading">
        <span v-if="!uploading">{{ editMode ? '수정' : '등록' }}</span>
        <span v-else> <i :class="$style.spinner"></i> </span>
      </button>
    </div>
  </paper-modal-container>
</template>

<script>
import axios from "axios";
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";
import { createToast } from "mosha-vue-toastify";
import "mosha-vue-toastify/dist/style.css";
import { useUserStore } from "@/store/user";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";
import { VIDEO_CREW_ROLE_OPTIONS, getArtistTypeDisplayName, getArtistTypeIconKey } from "@/utils/artistTypes";
import AutoComplete from "primevue/autocomplete";

export default {
  name: "VideoForm",
  components: {
    PaperModalContainer,
    AutoComplete,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: () => null,
    },
    defaultArtist: {
      type: String,
      default: "",
    },
    defaultArtistImage: {
      type: String,
      default: "",
    },
  },
  emits: ["close-popup", "upload-success", "update-success"],
  data() {
    return {
      userStore: useUserStore(),
      link: "",
      selectedType: "",
      isCover: true,
      originalSong: "",
      originalSongPreview: null, // 원곡 영상 정보 (title, channel, videoThumbnail 등)
      description: "",
      uploading: false,
      videoPreview: null,
      typeOptions: [
        { value: "solo", label: "솔로", emoji: "🎤" },
        { value: "duet", label: "듀엣", emoji: "👥" },
        { value: "team", label: "팀", emoji: "👨‍👩‍👧‍👦" },
        { value: "dance", label: "댄스", emoji: "💃" },
        { value: "virtual", label: "버츄얼", emoji: "🎭" },
        { value: "band", label: "밴드", emoji: "🎸" },
      ],
      // 라인업 관련 데이터 (LiveForm과 동일: 객체 배열 + 프로필 이미지)
      allArtists: [],
      filteredArtists: [],
      selectedArtists: [],
      selectedArtistData: null,
      currentArtistInput: "",
      artistImageLoadPromises: {},
      // 함께한 사람들 관련 데이터 (역할 섹션 → 해당 역할에 사람 추가, LiveForm과 동일)
      crewRoleSections: [],
      crewInputByRole: {},
      filteredCollaboratorsByRole: {},
      crewRoleOptions: [...VIDEO_CREW_ROLE_OPTIONS],
      currentCrewRoleInput: "",
      filteredCrewRoles: [],
      selectedCollaborators: [],
      crewSelectedDataByRole: {},
    };
  },
  computed: {
    pendingCrewRole() {
      return (this.currentCrewRoleInput || "").trim();
    },
    crewSections() {
      const roleKey = (r) => this.normalizeCrewRoleKey(r || "");
      const sections = [];
      for (const role of this.crewRoleSections) {
        const key = roleKey(role);
        const members = this.selectedCollaborators
          .map((person, index) => {
            if (roleKey(person.role) !== key) return null;
            return { person, index };
          })
          .filter(Boolean);
        sections.push({ role, members });
      }
      return sections;
    },
  },
  watch: {
    editData: {
      immediate: true,
      handler(newData) {
        if (newData && this.editMode) {
          this.link = newData.link || "";
          this.selectedType = newData.type || "";
          this.isCover = newData.isCover !== undefined ? newData.isCover : true;
          this.originalSong = newData.originalSong || "";
          this.originalSongPreview = newData.originalSongInfo || null;
          this.description = newData.description || "";
          this.selectedArtists = this.normalizeLineupForDisplay(newData.lineup);
          this.selectedCollaborators = this.normalizeCollaborators(
            newData.collaboratorDetails || newData.collaborators
          );
          const roleOrder = [];
          const roleKeysSeen = new Set();
          for (const item of this.selectedCollaborators) {
            const key = this.normalizeCrewRoleKey(item.role);
            if (!roleKeysSeen.has(key)) {
              roleKeysSeen.add(key);
              roleOrder.push(item.role);
            }
          }
          this.crewRoleSections = roleOrder;
          this.videoPreview = {
            title: newData.title,
            channel: newData.channel,
            channelThumbnail: newData.channelThumbnail,
            videoThumbnail: `https://img.youtube.com/vi/${newData.id}/sddefault.jpg`,
            publishedAt: newData.date || null,
          };
          this.hydrateSelectedPeopleFromArtistCards();
        }
      },
    },
    isVisible: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.renew();
        } else {
          // 팝업이 열릴 때(초기 true 포함) 아티스트 목록 로드
          this.fetchArtists();
        }
      },
    },
  },
  methods: {
    // 아티스트 목록 가져오기 (LiveForm과 동일: id, name, type, label 등)
    async fetchArtists() {
      try {
        const artistsSnapshot = await getDocs(collection(firestore, "artists"));
        const artistsData = [];
        artistsSnapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.approved) {
            artistsData.push({
              id: d.id,
              name: data.name || d.id,
              type: data.type || null,
              imgFileName: data.img || null,
              userProfileImg: data.userProfileImg || null,
              profileImage: null,
              profileImageLoaded: false,
              label: data.name || d.id,
            });
          }
        });
        this.allArtists = artistsData.sort((a, b) => a.name.localeCompare(b.name));
        this.ensureSelfArtistInLineup();
        this.hydrateSelectedPeopleFromArtistCards();
      } catch (error) {
        console.error("아티스트 목록 로드 실패:", error);
        this.allArtists = [];
      }
    },
    findArtistCardMatch(person) {
      if (!person || !Array.isArray(this.allArtists) || this.allArtists.length === 0) return null;
      const name = (person.name || "").toLowerCase();
      const id = person.id || null;
      return (
        this.allArtists.find((a) => id && a.id === id) ||
        this.allArtists.find((a) => name && (a.name || "").toLowerCase() === name) ||
        this.allArtists.find((a) => name && (a.label || "").toLowerCase() === name) ||
        null
      );
    },
    hydrateSelectedPeopleFromArtistCards() {
      if (!Array.isArray(this.allArtists) || this.allArtists.length === 0) return;

      if (Array.isArray(this.selectedArtists) && this.selectedArtists.length > 0) {
        this.selectedArtists = this.selectedArtists.map((artist) => {
          const matched = this.findArtistCardMatch(artist);
          if (!matched) return artist;
          return {
            ...artist,
            id: artist.id || matched.id || null,
            type: artist.type || matched.type || null,
            imgFileName: artist.imgFileName || matched.imgFileName || null,
            userProfileImg: artist.userProfileImg || matched.userProfileImg || null,
            profileImage: artist.profileImage || matched.profileImage || null,
            isNew: false,
          };
        });
      }

      if (Array.isArray(this.selectedCollaborators) && this.selectedCollaborators.length > 0) {
        this.selectedCollaborators = this.selectedCollaborators.map((person) => {
          const matched = this.findArtistCardMatch(person);
          if (!matched) return person;
          return {
            ...person,
            id: person.id || matched.id || null,
            type: person.type || matched.type || null,
            imgFileName: person.imgFileName || matched.imgFileName || null,
            userProfileImg: person.userProfileImg || matched.userProfileImg || null,
            profileImage: person.profileImage || matched.profileImage || null,
            isNew: false,
          };
        });
      }

      this.loadProfileImagesForArtists([
        ...this.selectedArtists,
        ...this.selectedCollaborators,
      ]).catch(() => {});
    },
    getSelfArtistNameCandidate() {
      return (this.defaultArtist || "").trim();
    },
    ensureSelfArtistInLineup() {
      // 작성(등록) 시 본인 아티스트를 라인업에 자동 포함
      if (this.editMode) return;
      if (!this.userStore?.isLogin) return;

      const selfName = this.getSelfArtistNameCandidate();
      if (!selfName) return;
      if (this.selectedArtists.some((a) => this.getArtistName(a) === selfName)) return;

      const matched =
        this.allArtists.find((a) => a.name === selfName || a.label === selfName) ||
        this.allArtists.find((a) => (a.name || "").toLowerCase() === selfName.toLowerCase());

      const presetImage = (this.defaultArtistImage || "").trim();
      const addedArtist = matched
        ? {
          ...matched,
          profileImage: presetImage || matched.profileImage || null,
          profileImageLoaded: !!presetImage || !!matched.profileImageLoaded,
          isNew: false,
        }
        : {
          name: selfName,
          profileImage: presetImage || null,
          id: null,
          type: null,
          imgFileName: null,
          userProfileImg: null,
          profileImageLoaded: !!presetImage,
          isNew: true,
        };

      this.selectedArtists.unshift(addedArtist);
      if (!presetImage && (addedArtist.id || addedArtist.imgFileName || addedArtist.userProfileImg)) {
        this.loadArtistProfileImage(addedArtist);
      }
    },
    getArtistName(artist) {
      if (artist && typeof artist === "object") return artist.name || "";
      return artist || "";
    },
    formatArtistType(type) {
      return getArtistTypeDisplayName(type);
    },
    getArtistDefaultImage(type) {
      try {
        const iconKey = getArtistTypeIconKey(type);
        return require(`@/assets/images/${iconKey}.png`);
      } catch {
        return require("@/assets/images/default_profile.png");
      }
    },
    getArtistImagePath(artist) {
      if (!artist) return null;
      if (artist.userProfileImg) return `profile/${artist.userProfileImg}`;
      if (artist.imgFileName) return `artist/${artist.imgFileName}`;
      return null;
    },
    syncArtistImageToAllArtists(artist) {
      if (!artist) return;
      const base = this.allArtists.find((a) => a.id === artist.id || a.name === artist.name);
      if (base && base !== artist) {
        base.profileImage = artist.profileImage || null;
        base.profileImageLoaded = true;
      }
    },
    async loadArtistProfileImage(artist) {
      if (!artist || artist.profileImageLoaded) return;
      if (artist.profileImage && typeof artist.profileImage === "string") {
        artist.profileImageLoaded = true;
        this.syncArtistImageToAllArtists(artist);
        return;
      }
      const imagePath = this.getArtistImagePath(artist);
      if (!imagePath) {
        artist.profileImageLoaded = true;
        this.syncArtistImageToAllArtists(artist);
        return;
      }
      const cacheKey = artist.id || artist.name || imagePath;
      if (this.artistImageLoadPromises[cacheKey]) {
        await this.artistImageLoadPromises[cacheKey];
        if (!artist.profileImage) {
          const base = this.allArtists.find((a) => a.id === artist.id || a.name === artist.name);
          if (base?.profileImage) artist.profileImage = base.profileImage;
        }
        artist.profileImageLoaded = true;
        return;
      }
      const loadPromise = (async () => {
        try {
          const imageRef = storageRef(storage, imagePath);
          artist.profileImage = await getDownloadURL(imageRef);
        } catch {
          artist.profileImage = null;
        } finally {
          artist.profileImageLoaded = true;
          this.syncArtistImageToAllArtists(artist);
        }
      })();
      this.artistImageLoadPromises[cacheKey] = loadPromise;
      try {
        await loadPromise;
      } finally {
        delete this.artistImageLoadPromises[cacheKey];
      }
    },
    async loadProfileImagesForArtists(artists) {
      if (!Array.isArray(artists) || artists.length === 0) return;
      const seen = new Set();
      const unique = artists.filter((a) => {
        const key = a?.id || a?.name;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      await Promise.all(unique.map((a) => this.loadArtistProfileImage(a)));
    },
    // 아티스트 검색 (공백일 때는 결과 없음)
    async searchArtists(event) {
      const query = (event && event.query != null ? event.query : "").toString().toLowerCase().trim();
      this.filteredArtists = this.allArtists.filter(
        (artist) =>
          artist.name.toLowerCase().includes(query) &&
          !this.selectedArtists.some((a) => this.getArtistName(a) === artist.name)
      );
      await this.loadProfileImagesForArtists(this.filteredArtists.slice(0, 20));
    },
    onArtistFocus() {
      this.searchArtists({ query: this.currentArtistInput || "" });
    },
    addArtistFromSuggestion(event) {
      const selectedArtist = event.value;
      if (selectedArtist) {
        this.selectedArtistData = selectedArtist;
        this.currentArtistInput = selectedArtist.label;
        this.loadArtistProfileImage(selectedArtist);
        this.addArtistManually();
      }
    },
    addArtistManually() {
      const inputName = (this.currentArtistInput || "").trim();
      const isSelectedMatch =
        this.selectedArtistData &&
        (!inputName ||
          inputName === this.selectedArtistData.name ||
          inputName === this.selectedArtistData.label);

      let artistName = "";
      let addedArtist = null;

      if (this.selectedArtistData && isSelectedMatch) {
        artistName = this.selectedArtistData.name;
        addedArtist = {
          name: artistName,
          profileImage: this.selectedArtistData.profileImage || null,
          id: this.selectedArtistData.id || null,
          type: this.selectedArtistData.type || null,
          imgFileName: this.selectedArtistData.imgFileName || null,
          userProfileImg: this.selectedArtistData.userProfileImg || null,
          profileImageLoaded: !!this.selectedArtistData.profileImageLoaded,
          isNew: false,
        };
      } else if (inputName) {
        artistName = inputName;
        addedArtist = {
          name: artistName,
          profileImage: null,
          id: null,
          type: null,
          imgFileName: null,
          userProfileImg: null,
          profileImageLoaded: true,
          isNew: true,
        };
      } else {
        this.toast("아티스트명을 입력해주세요", "warning");
        return;
      }

      if (this.selectedArtists.some((a) => this.getArtistName(a) === artistName)) {
        this.toast("이미 추가된 아티스트입니다", "warning");
        return;
      }

      this.selectedArtists.push(addedArtist);
      if (addedArtist.id || addedArtist.imgFileName || addedArtist.userProfileImg) {
        this.loadArtistProfileImage(addedArtist);
      }
      this.clearArtistInput();
    },
    clearArtistInput() {
      this.currentArtistInput = "";
      this.selectedArtistData = null;
      this.filteredArtists = [];
      this.$nextTick(() => {
        if (this.$refs.artistAutocompleteRef?.$el?.querySelector("input")) {
          this.$refs.artistAutocompleteRef.$el.querySelector("input").value = "";
        }
      });
    },
    isDefaultArtist(artist) {
      const selfName = this.getSelfArtistNameCandidate();
      if (!selfName) return false;
      return this.getArtistName(artist) === selfName;
    },
    // 아티스트 제거
    removeArtist(index) {
      const artist = this.selectedArtists[index];
      if (artist && this.isDefaultArtist(artist)) return;
      this.selectedArtists.splice(index, 1);
    },
    normalizeCrewRole(role) {
      return (role || "").trim().replace(/\s+/g, " ");
    },
    normalizeCrewRoleKey(role) {
      return this.normalizeCrewRole(role).toLowerCase();
    },
    findCrewRoleSection(role) {
      const key = this.normalizeCrewRoleKey(role);
      if (!key) return "";
      return this.crewRoleSections.find((r) => this.normalizeCrewRoleKey(r) === key) || "";
    },
    addCrewRoleSection() {
      const role = this.normalizeCrewRole(this.pendingCrewRole);
      if (!role) {
        this.toast("역할명을 입력해주세요", "warning");
        return;
      }
      if (this.findCrewRoleSection(role)) {
        this.toast("이미 추가된 역할 섹션입니다", "warning");
        return;
      }
      this.crewRoleSections.push(role);
      this.currentCrewRoleInput = "";
      this.filteredCrewRoles = [];
    },
    onCrewRoleFocus() {
      this.searchCrewRoles({ query: this.currentCrewRoleInput || "" });
    },
    searchCrewRoles(event) {
      const query = (event && event.query != null ? event.query : "").toString().toLowerCase().trim();
      this.filteredCrewRoles = this.crewRoleOptions.filter((r) =>
        r.toLowerCase().includes(query)
      );
    },
    addCrewRoleFromSuggestion(event) {
      const selectedRole = typeof event.value === "string" ? event.value : "";
      this.currentCrewRoleInput = selectedRole;
      this.addCrewRoleSection();
    },
    async removeCrewRoleSection(role) {
      const targetRole = this.findCrewRoleSection(role) || this.normalizeCrewRole(role);
      const targetKey = this.normalizeCrewRoleKey(targetRole);
      const hasMembers = this.selectedCollaborators.some(
        (item) => this.normalizeCrewRoleKey(item.role) === targetKey
      );
      if (hasMembers) {
        const confirmed = await this.$confirm(
          `${targetRole} 섹션의 인원도 함께 삭제됩니다. 진행할까요?`
        );
        if (!confirmed) return;
      }
      this.crewRoleSections = this.crewRoleSections.filter(
        (r) => this.normalizeCrewRoleKey(r) !== targetKey
      );
      this.selectedCollaborators = this.selectedCollaborators.filter(
        (item) => this.normalizeCrewRoleKey(item.role) !== targetKey
      );
      delete this.crewInputByRole[targetRole];
      delete this.filteredCollaboratorsByRole[targetRole];
      delete this.crewSelectedDataByRole[targetRole];
    },
    canAddCollaboratorForRole(role) {
      const input = (this.crewInputByRole[role] || "").trim();
      return !!input;
    },
    async searchCollaboratorsByRole(event, role) {
      const query = (event.query || "").toLowerCase().trim();
      const roleKey = this.normalizeCrewRoleKey(role);
      const list = this.allArtists.filter(
        (artist) =>
          (artist.name || "").toLowerCase().includes(query) &&
          !this.selectedCollaborators.some(
            (item) =>
              item.name === artist.name && this.normalizeCrewRoleKey(item.role) === roleKey
          )
      );
      this.filteredCollaboratorsByRole[role] = list;
      await this.loadProfileImagesForArtists(list.slice(0, 20));
    },
    addCollaboratorFromSuggestionByRole(event, role) {
      const selected = event.value;
      if (!selected) return;
      this.crewSelectedDataByRole[role] = selected;
      this.crewInputByRole[role] = typeof selected === "object" ? (selected.label || selected.name) : selected;
      this.loadArtistProfileImage(selected);
      this.addCollaboratorManuallyByRole(role);
    },
    addCollaboratorManuallyByRole(role) {
      const inputName = (this.crewInputByRole[role] || "").trim();
      const sectionRole = this.findCrewRoleSection(role) || this.normalizeCrewRole(role);
      const roleKey = this.normalizeCrewRoleKey(sectionRole);
      const selectedData = this.crewSelectedDataByRole[role];
      const isSelectedMatch = selectedData && (
        !inputName ||
        inputName === (selectedData.name || "") ||
        inputName === (selectedData.label || "")
      );

      let name = "";
      let added = null;

      if (selectedData && isSelectedMatch) {
        name = selectedData.name || selectedData.label || "";
        added = {
          name,
          profileImage: selectedData.profileImage || null,
          id: selectedData.id || null,
          type: selectedData.type || null,
          imgFileName: selectedData.imgFileName || null,
          userProfileImg: selectedData.userProfileImg || null,
          profileImageLoaded: !!selectedData.profileImageLoaded,
          isNew: false,
        };
      } else if (inputName) {
        name = inputName;
        added = {
          name,
          profileImage: null,
          id: null,
          type: null,
          imgFileName: null,
          userProfileImg: null,
          profileImageLoaded: true,
          isNew: true,
        };
      } else {
        this.toast("이름을 입력해주세요", "warning");
        return;
      }

      if (!sectionRole) {
        this.toast("역할명을 입력해주세요", "warning");
        return;
      }

      added.role = sectionRole;

      if (
        this.selectedCollaborators.some(
          (c) =>
            this.getCrewName(c) === name &&
            this.normalizeCrewRoleKey(c.role) === roleKey
        )
      ) {
        this.toast("해당 역할에 이미 추가된 이름입니다", "warning");
        return;
      }

      this.selectedCollaborators.push(added);
      if (added.id || added.imgFileName || added.userProfileImg) {
        this.loadArtistProfileImage(added);
      }
      this.crewInputByRole[role] = "";
      this.crewSelectedDataByRole[role] = null;
      this.filteredCollaboratorsByRole[role] = [];
    },
    getCrewName(person) {
      if (typeof person === "object" && person !== null) {
        return person.name || "";
      }
      return person || "";
    },
    normalizeLineupForDisplay(lineupRaw) {
      if (!Array.isArray(lineupRaw)) return [];
      return lineupRaw.map((item) => {
        if (typeof item === "string") {
          return { name: item, profileImage: null, type: null, isNew: false };
        }
        if (item && typeof item === "object" && item.name) {
          return {
            name: item.name,
            profileImage: item.profileImage || null,
            type: item.type || null,
            id: item.id || null,
            isNew: item.isNew || false,
          };
        }
        return null;
      }).filter(Boolean);
    },
    normalizeCollaborators(collaboratorsRaw) {
      if (!Array.isArray(collaboratorsRaw)) return [];
      return collaboratorsRaw
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") {
            return { role: "기타", name: item, profileImage: null, type: null, isNew: false };
          }
          if (typeof item === "object") {
            const name = item.name || item.collaborator || "";
            const role = item.role || "기타";
            if (!name) return null;
            return {
              role,
              name,
              profileImage: item.profileImage || null,
              type: item.type || null,
              id: item.id || null,
              isNew: item.isNew || false,
            };
          }
          return null;
        })
        .filter(Boolean);
    },
    removeCollaborator(index) {
      this.selectedCollaborators.splice(index, 1);
    },
    cancelPopup() {
      this.$emit("close-popup");
    },
    handleChannelImageError() {
      // 채널 이미지 로드 실패 시 fallback으로 전환
      if (this.videoPreview) {
        this.videoPreview = {
          ...this.videoPreview,
          channelThumbnail: null,
        };
      }
    },
    formatPublishedAt(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}년 ${month}월 ${day}일`;
    },
    renew() {
      this.selectedDateTime = new Date();
      this.link = "";
      this.selectedType = "";
      this.isCover = true;
      this.originalSong = "";
      this.originalSongPreview = null;
      this.description = "";
      this.uploading = false;
      this.videoPreview = null;
      // 라인업 초기화
      this.selectedArtists = [];
      this.selectedArtistData = null;
      this.currentArtistInput = "";
      this.filteredArtists = [];
      // 함께한 사람들 초기화
      this.crewRoleSections = [];
      this.crewInputByRole = {};
      this.filteredCollaboratorsByRole = {};
      this.crewSelectedDataByRole = {};
      this.currentCrewRoleInput = "";
      this.filteredCrewRoles = [];
      this.selectedCollaborators = [];
    },
    async onLinkBlur() {
      if (!this.link || this.uploading) return;

      const vid = this.extractYouTubeVideoId(this.link);
      if (!vid) {
        this.videoPreview = null;
        return;
      }

      try {
        const vInfo = await this.fetchVideoDetailsQuick(vid);
        if (vInfo) {
          // 먼저 채널 썸네일 없이 빠르게 미리보기 표시
          this.videoPreview = {
            title: vInfo.title,
            channel: vInfo.channel,
            channelId: vInfo.channelId,
            publishedAt: vInfo.publishedAt,
            channelThumbnail: null,
            videoThumbnail: vInfo.videoThumbnail,
          };

          // 채널 썸네일은 비동기로 가져와서 업데이트
          this.loadChannelThumbnailAsync(vInfo.channelId);
        } else {
          this.videoPreview = null;
        }
      } catch (error) {
        this.videoPreview = null;
      }
    },
    async loadChannelThumbnailAsync(channelId) {
      if (!channelId) return;

      try {
        const { channelThumbnail, channelHandle } = await this.fetchChannelInfo(channelId);
        if (this.videoPreview) {
          this.videoPreview = {
            ...this.videoPreview,
            channelThumbnail: channelThumbnail || this.videoPreview.channelThumbnail,
            channelHandle: channelHandle || this.videoPreview.channelHandle,
          };
        }
      } catch (e) {
        console.warn("채널 정보 가져오기 실패:", e);
      }
    },
    async onOriginalSongBlur() {
      const url = (this.originalSong || "").trim();
      if (!url || this.uploading) {
        this.originalSongPreview = null;
        return;
      }
      const vid = this.extractYouTubeVideoId(url);
      if (!vid) {
        this.originalSongPreview = null;
        return;
      }
      try {
        const vInfo = await this.fetchVideoDetailsQuick(vid);
        if (vInfo) {
          this.originalSongPreview = {
            id: vid,
            title: vInfo.title,
            channel: vInfo.channel,
            channelId: vInfo.channelId,
            publishedAt: vInfo.publishedAt,
            channelThumbnail: null,
            videoThumbnail: vInfo.videoThumbnail,
          };
          this.loadOriginalSongChannelThumbnailAsync(vInfo.channelId);
        } else {
          this.originalSongPreview = null;
        }
      } catch (error) {
        this.originalSongPreview = null;
      }
    },
    async loadOriginalSongChannelThumbnailAsync(channelId) {
      if (!channelId || !this.originalSongPreview) return;
      try {
        const { channelThumbnail, channelHandle } = await this.fetchChannelInfo(channelId);
        if (this.originalSongPreview) {
          this.originalSongPreview = {
            ...this.originalSongPreview,
            channelThumbnail: channelThumbnail || this.originalSongPreview.channelThumbnail,
            channelHandle: channelHandle || this.originalSongPreview.channelHandle,
          };
        }
      } catch (e) {
        console.warn("원곡 채널 정보 가져오기 실패:", e);
      }
    },
    handleOriginalSongChannelImageError() {
      if (this.originalSongPreview) {
        this.originalSongPreview = { ...this.originalSongPreview, channelThumbnail: null };
      }
    },
    toast(msg, type) {
      createToast(
        {
          title: msg,
          // description: 'Toast description'
        },
        {
          type: type, // 'info', 'danger', 'warning', 'success', 'default'
          timeout: 5000,
          showCloseButton: true,
          position: "top-right", // 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'bottom-center'
          transition: "bounce",
          hideProgressBar: false,
          swipeClose: true,
          // toastBackgroundColor: '#fafafa',
          onClose: null,
        }
      );
    },
    async upload() {
      if (!this.checkUrl(this.link)) {
        this.toast("YOUTUBE 링크를 입력해주세요", "default");
        return;
      }

      if (this.link.includes("@")) {
        this.toast("채널주소는 등록할 수 없습니다", "danger");
        return;
      }

      // 원곡 링크 유효성 검사
      if (this.isCover && this.originalSong.trim() && !this.isValidYouTubeUrl(this.originalSong.trim())) {
        this.toast("원곡은 유효한 유튜브 링크를 입력해주세요", "warning");
        return;
      }

      const vid = this.extractYouTubeVideoId(this.link);
      if (!vid) {
        this.toast("유효한 유튜브 링크가 아닙니다", "danger");
        return;
      }

      try {
        this.uploading = true;

        const videoDocRef = doc(firestore, "videos", vid);
        const existingDoc = await getDoc(videoDocRef);

        if (existingDoc.exists()) {
          this.toast("이미 등록된 영상입니다", "warning");
          this.renew();
          return;
        }

        const vInfo = await this.fetchVideoDetails(vid);
        if (!vInfo) {
          this.toast("유튜브 연결에 실패했습니다.", "danger");
          this.renew();
          return;
        }

        // 유튜브 업로드일이 없는 경우에만 현재 시각 문자열로 대체
        const nowIso = new Date().toISOString().replace("T", " ").replace("Z", "");

        // 유튜브 업로드일 포맷팅
        const youtubeDate = vInfo.publishedAt
          ? new Date(vInfo.publishedAt).toISOString().replace("T", " ").replace("Z", "")
          : nowIso;

        const videoData = {
          id: vid,
          title: vInfo.title,
          channel: vInfo.channel,
          channelId: vInfo.channelId || null,
          channelThumbnail: vInfo.channelThumbnail || null,
          channelHandle: vInfo.channelHandle || null,
          link: this.link,
          date: youtubeDate, // 유튜브 업로드일 기준 정렬용
          registeredAt: serverTimestamp(), // 실제 등록일(Timestamp)
          type: this.selectedType || null,
          isCover: this.isCover,
          originalSong: this.isCover && this.originalSong.trim() && this.isValidYouTubeUrl(this.originalSong.trim())
            ? this.originalSong.trim()
            : null,
          originalSongInfo: this.isCover && this.originalSongPreview
            ? {
              id: this.originalSongPreview.id,
              title: this.originalSongPreview.title,
              channel: this.originalSongPreview.channel || null,
              channelThumbnail: this.originalSongPreview.channelThumbnail || null,
              videoThumbnail: this.originalSongPreview.videoThumbnail || null,
              publishedAt: this.originalSongPreview.publishedAt || null,
            }
            : null,
          description: this.description.trim() || null,
          likes: 0,
          // 라인업
          lineup: this.selectedArtists.length > 0 ? this.selectedArtists.map((a) => this.getArtistName(a)) : null,
          // 함께한 사람들
          collaborators:
            this.selectedCollaborators.length > 0
              ? this.selectedCollaborators.map((p) => this.getCrewName(p))
              : null,
          collaboratorDetails:
            this.selectedCollaborators.length > 0
              ? this.selectedCollaborators.map((p) => ({ role: p.role, name: this.getCrewName(p) }))
              : null,
          // 등록자 정보
          registeredBy: this.userStore.email || null,
          registeredByName: this.userStore.nickname || this.userStore.name || null,
          registeredByProfileImage: this.userStore.profileImage || null,
        };

        // ✅ 문서가 없을 때만 등록
        await setDoc(videoDocRef, videoData);

        // 활동 추적
        trackActivity(ACTIVITY_TYPES.VIDEO_CREATE, {
          target: "video",
          targetId: vid,
          metadata: {
            title: vInfo.title,
            channel: vInfo.channel,
            type: this.selectedType || null,
          },
        });

        this.cancelPopup();
        this.toast("링크 등록 완료!", "default");
        this.renew();
        this.$emit("upload-success");
      } catch (error) {
        this.toast("링크 등록에 실패했습니다", "danger");
        this.renew();
        console.error("Error uploading data:", error);
      }
    },
    // 비디오 수정
    async updateVideo() {
      if (!this.editData || !this.editData.id) {
        this.toast("수정할 영상 정보가 없습니다", "danger");
        return;
      }

      // 원곡 링크 유효성 검사
      if (this.isCover && this.originalSong.trim() && !this.isValidYouTubeUrl(this.originalSong.trim())) {
        this.toast("원곡은 유효한 유튜브 링크를 입력해주세요", "warning");
        return;
      }

      try {
        this.uploading = true;

        const videoDocRef = doc(firestore, "videos", this.editData.id);

        const updateData = {
          type: this.selectedType || null,
          isCover: this.isCover,
          originalSong: this.isCover && this.originalSong.trim() && this.isValidYouTubeUrl(this.originalSong.trim())
            ? this.originalSong.trim()
            : null,
          originalSongInfo: this.isCover && this.originalSongPreview
            ? {
              id: this.originalSongPreview.id,
              title: this.originalSongPreview.title,
              channel: this.originalSongPreview.channel || null,
              channelThumbnail: this.originalSongPreview.channelThumbnail || null,
              videoThumbnail: this.originalSongPreview.videoThumbnail || null,
              publishedAt: this.originalSongPreview.publishedAt || null,
            }
            : null,
          description: this.description.trim() || null,
          lineup: this.selectedArtists.length > 0 ? this.selectedArtists.map((a) => this.getArtistName(a)) : null,
          collaborators:
            this.selectedCollaborators.length > 0
              ? this.selectedCollaborators.map((p) => this.getCrewName(p))
              : null,
          collaboratorDetails:
            this.selectedCollaborators.length > 0
              ? this.selectedCollaborators.map((p) => ({ role: p.role, name: this.getCrewName(p) }))
              : null,
          updatedAt: serverTimestamp(),
          updatedBy: this.userStore.email || null,
        };

        await updateDoc(videoDocRef, updateData);

        this.cancelPopup();
        this.toast("영상 정보가 수정되었습니다!", "default");
        this.renew();
        this.$emit("update-success");
      } catch (error) {
        this.toast("영상 수정에 실패했습니다", "danger");
        console.error("Error updating video:", error);
      } finally {
        this.uploading = false;
      }
    },
    // 유튜브 비디오 ID 추출 함수
    extractYouTubeVideoId(url) {
      let videoId = null;

      // 1️⃣ `youtu.be/VIDEO_ID` 형식인 경우
      if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split(/[?&]/)[0];
      }
      // 2️⃣ `youtube.com/watch?v=VIDEO_ID` 형식인 경우
      else if (url.includes("youtube.com/watch?v=")) {
        videoId = url.split("v=")[1].split(/[?&]/)[0];
      }
      return videoId;
    },
    // 빠른 영상 정보 조회 (채널 썸네일 제외)
    async fetchVideoDetailsQuick(vid) {
      const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${vid}&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`;
      try {
        const response = await axios.get(url);
        const items = response.data.items;

        if (items.length > 0) {
          const snippet = items[0].snippet;
          const title = snippet.title;
          const channel = snippet.channelTitle;
          const channelId = snippet.channelId;
          const videoDescription = snippet.description || "";
          const videoThumbnail = snippet.thumbnails?.maxres?.url || snippet.thumbnails?.standard?.url || snippet.thumbnails?.high?.url || snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url || null;
          const publishedAt = snippet.publishedAt || null; // 유튜브 업로드 날짜

          return { title, channel, channelId, videoThumbnail, videoDescription, publishedAt };
        } else {
          this.$alert("❌ 영상을 찾을 수 없습니다.");
          return null;
        }
      } catch (error) {
        this.$alert(
          "❌ YOUTUBE API 요청 실패:",
          error.response?.data || error.message
        );
        return null;
      }
    },
    // 전체 영상 정보 조회 (채널 썸네일 포함 - 등록 시 사용)
    async fetchVideoDetails(vid) {
      const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${vid}&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`;
      try {
        const response = await axios.get(url);
        const items = response.data.items;

        if (items.length > 0) {
          const snippet = items[0].snippet;
          const title = snippet.title;
          const channel = snippet.channelTitle;
          const channelId = snippet.channelId;
          const videoDescription = snippet.description || ""; // 영상 설명 추가

          // 영상 썸네일 가져오기 (고해상도 우선: maxres 1280x720 → standard 640x480 → high 480x360)
          const videoThumbnail = snippet.thumbnails?.maxres?.url || snippet.thumbnails?.standard?.url || snippet.thumbnails?.high?.url || snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url || null;
          const publishedAt = snippet.publishedAt || null; // 유튜브 업로드 날짜

          // 채널 정보(썸네일, 핸들) 가져오기
          let channelThumbnail = null;
          let channelHandle = null;
          try {
            const channelInfo = await this.fetchChannelInfo(channelId);
            channelThumbnail = channelInfo.channelThumbnail;
            channelHandle = channelInfo.channelHandle;
          } catch (e) {
            console.warn("채널 정보 가져오기 실패:", e);
          }

          return { title, channel, channelId, channelThumbnail, channelHandle, videoThumbnail, videoDescription, publishedAt };
        } else {
          this.$alert("❌ 영상을 찾을 수 없습니다.");
          return null;
        }
      } catch (error) {
        this.$alert(
          "❌ YOUTUBE API 요청 실패:",
          error.response?.data || error.message
        );
        return null;
      }
    },
    /** 채널 썸네일과 핸들(customUrl) 조회 */
    async fetchChannelInfo(channelId) {
      const url = `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`;
      try {
        const response = await axios.get(url);
        const items = response.data.items;

        if (items.length > 0) {
          const snippet = items[0].snippet;
          const thumbnails = snippet.thumbnails;
          const channelThumbnail = thumbnails?.medium?.url || thumbnails?.default?.url || null;
          const customUrl = snippet.customUrl || null;
          const channelHandle = customUrl ? (customUrl.startsWith("@") ? customUrl : `@${customUrl}`) : null;
          return { channelThumbnail, channelHandle };
        }
        return { channelThumbnail: null, channelHandle: null };
      } catch (error) {
        console.error("채널 정보 API 요청 실패:", error);
        return { channelThumbnail: null, channelHandle: null };
      }
    },

    formatDate(date) {
      if (!(date instanceof Date) || isNaN(date)) {
        return "유효하지 않은 날짜";
      }
      // 날짜 구성
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // 월 (0부터 시작)
      const day = String(date.getDate()).padStart(2, "0");

      // 시간 구성
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      // 원하는 형식으로 반환
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    },
    checkUrl(url) {
      const httpsPattern = /^https:\/\/[^\s$.?#].[^\s]*$/i;
      return httpsPattern.test(url);
    },
    isValidYouTubeUrl(url) {
      if (!url) return false;
      // 유튜브 URL 패턴 체크
      return url.includes("youtube.com/watch?v=") || url.includes("youtu.be/");
    },
  },
};
</script>
<style module>
h4 {
  font-family: "Paperlogy7", sans-serif;
  font-size: 14px;
  margin: 0 0 8px 0;
  color: #444;
  font-weight: 600;
  position: relative;
}

h4 span {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 4px;
}

.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

.input {
  padding: 0;
  margin-bottom: 20px;
}

.helpText {
  font-size: 12px;
  color: #999;
  margin: 6px 0 0 0;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.5;
}

.preview {
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 0;
  margin: 10px 0 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.previewContent {
  display: flex;
  gap: 14px;
  align-items: stretch;
}

.videoThumbWrap {
  position: relative;
  flex-shrink: 0;
  width: 160px;
  min-width: 160px;
}

.videoThumb {
  width: 100%;
  height: 90px;
  object-fit: cover;
  border-radius: 0;
  display: block;
}

.previewBadge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.02em;
}

.previewInfo {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 12px 14px 12px 0;
}

.previewTitle {
  font-family: "NanumSquareRoundB", "Paperlogy6", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.previewMeta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.channelPreview {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.channelThumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.channelThumbFallback {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #66bdff 0%, #a8d8ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.channelName {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.previewDate {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
}

input {
  width: 100%;
  font-size: 14px;
  height: 40px !important;
  font-family: "NanumSquareRoundR", sans-serif;
}

.textInput {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.textInput::placeholder {
  color: #999;
}

.textInput:focus {
  outline: none;
  border-color: #66bdff;
}

.textInput:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.disabledInput {
  background: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.textarea {
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 5px;
  padding: 8px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
  resize: vertical;
  min-height: 120px;
  line-height: 1.4;
}

.textarea:focus {
  border: 1px solid #aaaeb7;
  outline: none;
}

.textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.selectInput {
  width: 100%;
  font-size: 13px;
  height: 28px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  padding: 0px 5px;
  background-color: white;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.selectInput:focus {
  border: 1px solid #aaaeb7;
  outline: none;
}

.selectInput:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

/* 타입 선택 / 분류 (LiveForm .radio와 동일 스타일) */
.typeSelector {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.typeButton {
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
  font-weight: 400;
  flex: 1;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.typeButton:hover:not(:disabled) {
  border-color: #66bdff;
  color: #66bdff;
}

.typeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.typeButtonActive {
  border-color: #66bdff;
  background: #66bdff;
  color: #fff;
  font-weight: 500;
}

.typeButtonActive .typeLabel {
  color: #fff;
}

.typeLabel {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: inherit;
  transition: color 0.15s ease;
}

/* LiveForm과 유사한 라인업/크루 입력 UI */
.artistList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
  margin-top: 10px;
}

.artistItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: #f8f9fa;
  border-radius: 8px;
}

.artistInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.profileImage {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.newBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 11px;
  color: #fff;
  line-height: 12px;
  background: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
}

.artistDetails {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.artistName {
  font-size: 15px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  word-break: break-word;
}

.artistNameRow {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.roleBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eaf4ff;
  color: #2b78c5;
  font-size: 11px;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.4;
}

.artistType {
  font-size: 12px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.removeButton {
  padding: 6px 12px;
  background: #f44336;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-left: 12px;
}

.removeButton:hover:not(:disabled) {
  background: #d32f2f;
}

.removeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* LiveForm과 동일: 아티스트 추가 폼 + 추가 버튼 */
.addArtistForm {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  position: relative;
}

.addArtistForm .artistSearchAutocomplete {
  flex: 1;
  width: 100%;
  position: relative;
}

.addArtistForm :global(.p-autocomplete) {
  flex: 1;
  min-width: 0;
  position: relative;
}

.addArtistForm :global(.p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.addArtistForm :global(.p-autocomplete-input:focus) {
  outline: none;
  border-color: #2196f3;
  box-shadow: none;
}

.addArtistForm :global(.p-autocomplete-input:disabled) {
  background: #f5f5f5;
  cursor: not-allowed;
}

.addArtistForm :global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  transition: all 0.2s;
}

.addArtistForm :global(.p-autocomplete-dropdown:hover) {
  background: #e9ecef;
}

.addArtistForm :global(.p-autocomplete:focus-within .p-autocomplete-dropdown) {
  border-color: #2196f3;
}

/* 라인업/함께한 사람들 드롭다운 패널: 입력란과 같은 너비 (appendTo="self" 사용 시) */
.addArtistForm :global(.p-autocomplete-panel) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  min-width: 100% !important;
  margin-top: 2px;
  box-sizing: border-box;
  z-index: 1100;
}

.addButton {
  padding: 10px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.addButton:hover:not(:disabled) {
  background: #1976d2;
}

.addButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 라인업 드롭다운 옵션 (LiveForm과 동일) */
:global(.artist-option) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 0;
  justify-content: flex-start;
  text-align: left;
}

:global(.option-profile-image) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

:global(.option-details) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  text-align: left;
}

:global(.option-name) {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

:global(.option-type) {
  font-size: 12px;
  color: #666;
}

/* 함께한 사람들 - 역할 섹션 (LiveForm과 동일) */
.infoText {
  font-size: 12px;
  color: #66bdff;
  margin: 8px 0;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.6;
  padding: 8px 12px;
  background: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #66bdff;
}

.crewRoleSelector {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 8px;
  position: relative;
}

.crewRoleAutocomplete {
  flex: 1;
  width: 100%;
  position: relative;
}

.crewRoleSelector :global(.p-autocomplete) {
  width: 100%;
  position: relative;
}

/* 역할 드롭다운 패널: 입력란과 같은 너비·위치, 텍스트 왼쪽 정렬 */
.crewRoleSelector :global(.video-crew-role-panel) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  min-width: 100% !important;
  margin-top: 2px;
  box-sizing: border-box;
  z-index: 1100;
  text-align: left;
}

.crewRoleSelector :global(.video-crew-role-panel .p-autocomplete-item) {
  text-align: left;
}

.crewRoleSelector :global(.p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.crewRoleSelector :global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 8px 8px 0;
}

.crewRoleSelector :global(.p-autocomplete-input:focus) {
  outline: none;
  border-color: #2196f3;
  box-shadow: none;
}

.crewRoleSelector :global(.p-autocomplete:focus-within .p-autocomplete-dropdown) {
  border-color: #2196f3;
}

.addRoleButton {
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  white-space: nowrap;
}

.addRoleButton:hover:not(:disabled) {
  background: #43a047;
}

.addRoleButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.crewDepthBlock {
  margin-left: 8px;
  padding-left: 12px;
  border-left: 2px solid #eceff3;
  margin-bottom: 8px;
}

.crewSectionList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.crewAddForm {
  margin-top: 4px;
}

.crewSection {
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.crewSectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.crewSectionTitle {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.removeRoleButton {
  padding: 3px 8px;
  border: 1px solid #eadede;
  border-radius: 6px;
  background: #fff;
  color: #8a5d5d;
  font-size: 11px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
}

.removeRoleButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.autocomplete {
  width: 100%;
  font-size: 14px;
  position: relative;
}

:global(.p-autocomplete) {
  width: 100%;
  display: flex;
  position: relative;
}

:global(.p-autocomplete-input) {
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 6px 0px 0px 6px;
  padding: 10px 14px;
  height: 40px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  font-family: "NanumSquareRoundR", sans-serif;
  flex: 1;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
}

:global(.p-autocomplete-input::placeholder) {
  color: #999;
}

:global(.p-autocomplete-input:focus) {
  border-color: #66bdff;
  border-right: none;
  outline: none;
  box-shadow: none;
}

:global(.p-autocomplete-input:focus)+ :global(.p-autocomplete-dropdown) {
  border-color: #66bdff;
  border-left: none;
}

:global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 6px 6px 0;
  height: 40px;
  background: #fff;
  color: #666;
  transition: border-color 0.15s ease;
  flex-shrink: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.p-autocomplete-dropdown:hover) {
  background: #fafafa;
  border-color: #66bdff;
}

:global(.p-autocomplete-panel) {
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #fff;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: auto;
  min-width: 100%;
  margin-top: 2px;
}

:global(.video-artist-autocomplete-panel) {
  z-index: 1200 !important;
}

:global(.p-autocomplete-item) {
  padding: 10px 14px;
  font-size: 14px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

:global(.p-autocomplete-item:last-child) {
  border-bottom: none;
}

:global(.p-autocomplete-item:hover) {
  background-color: #f8f9fa;
}

:global(.p-autocomplete-item-highlight) {
  background-color: #f0f7ff;
  color: #66bdff;
}

:global(.p-autocomplete-dropdown-icon) {
  color: #666;
  font-size: 12px;
  display: none;
}

:global(.p-autocomplete-loading-icon) {
  display: none;
}

:global(.p-autocomplete-empty-message) {
  padding: 0px 10px;
  font-size: 14px;
}

:global(.p-icon) {
  display: none;
}

.buttons {
  margin-top: 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.cancel {
  padding: 12px 24px;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cancel:hover:not(:disabled) {
  background: #e8e8e8;
  border-color: #ccc;
}

.cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.register {
  padding: 12px 24px;
  border-radius: 6px;
  background: #66bdff;
  color: #fff;
  border: none;
  min-width: 80px;
  font-size: 14px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.register:hover:not(:disabled) {
  background: #4a9eff;
}

.register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid transparent;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  box-sizing: border-box;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  h4 {
    font-size: 13px;
    margin: 0 0 6px 0;
  }

  h4 span {
    font-size: 11px !important;
  }

  input {
    font-size: 14px;
    height: 38px !important;
  }

  .textInput {
    padding: 8px 12px;
  }

  .input {
    margin-bottom: 18px;
  }

  .textarea {
    font-size: 12px;
    min-height: 100px;
    border-radius: 4px;
  }

  .previewContent {
    flex-direction: column;
  }

  .videoThumbWrap {
    width: 100%;
    min-width: 0;
  }

  .videoThumb {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .previewInfo {
    padding: 10px 12px 12px;
  }

  .previewTitle {
    font-size: 13px;
  }

  .channelThumb {
    width: 20px;
    height: 20px;
  }

  .channelThumbFallback {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .channelName {
    font-size: 11px;
  }

  .previewDate {
    font-size: 10px;
  }

  .helpText {
    font-size: 0.6875rem;
  }

  .typeSelector {
    gap: 6px;
  }

  .typeButton {
    font-size: 13px;
    padding: 8px 16px;
    min-width: 70px;
  }

  .typeLabel {
    font-size: 13px;
  }

  .buttons {
    margin-top: 20px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .cancel,
  .register {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  .spinner {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  :global(.p-autocomplete-input) {
    padding: 8px 12px;
    height: 38px;
    border-radius: 6px 0 0 6px;
  }

  :global(.p-autocomplete-dropdown) {
    height: 38px;
    width: 38px;
    border-radius: 0 6px 6px 0;
  }

  :global(.p-autocomplete-panel) {
    max-height: 180px;
  }

  :global(.p-autocomplete-item) {
    padding: 8px 12px;
    font-size: 13px;
  }

  .artistList {
    gap: 6px;
    margin-bottom: 12px;
  }

  .artistItem {
    padding: 10px 12px;
  }

  .artistName {
    font-size: 14px;
  }

  .artistNameRow {
    gap: 4px;
  }

  .roleBadge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .artistType {
    font-size: 11px;
  }

  .addArtistForm {
    flex-direction: column;
    gap: 6px;
  }

  .addButton {
    width: 100%;
    padding: 10px 16px;
  }

  .removeButton {
    padding: 5px 10px;
    font-size: 12px;
    margin-left: 10px;
  }

  .crewRoleSelector {
    flex-direction: column;
    align-items: stretch;
  }

  .addRoleButton {
    width: 100%;
  }

  .crewSection {
    padding: 6px;
  }

  .crewSectionTitle {
    font-size: 13px;
  }

  .infoText {
    font-size: 11px;
    padding: 6px 10px;
  }
}
</style>
